"use client";

import { useEffect } from "react";
import { flare } from "@flareapp/js";
import { shouldReloadForChunkError } from "@/lib/chunk-reload";

/**
 * Last-resort error boundary. It replaces the root layout (including the
 * next-intl provider), so it cannot use `useTranslations` — a short bilingual
 * (EN/TR) copy is inlined instead, which is standard for this fallback. Styles
 * are inline too, since the app's global stylesheet is not guaranteed here.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // A tab that outlived a deploy asked for a chunk the new build dropped.
    // React caught it here, so no window handler will see it — reload once and
    // skip the report, since the visitor never sees this boundary.
    if (shouldReloadForChunkError(error, window.sessionStorage)) {
      window.location.reload();
      return;
    }
    console.error(error);
    // Reported only when Flare is lit (instrumentation-client.ts, env-gated).
    if (process.env.NEXT_PUBLIC_FLARE_KEY) flare.report(error);
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: "100dvh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "1rem",
          padding: "1.5rem",
          textAlign: "center",
          background:
            "linear-gradient(180deg, #050507 0%, #0a0812 45%, #12081a 100%)",
          color: "#ffffff",
          fontFamily:
            "system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
        }}
      >
        <h1 style={{ fontSize: "1.5rem", fontWeight: 600, margin: 0 }}>
          Something went wrong
        </h1>
        <p style={{ maxWidth: "26rem", color: "rgba(255,255,255,0.6)", margin: 0 }}>
          An unexpected error occurred. Please try again.
          <br />
          Beklenmedik bir hata oluştu. Lütfen tekrar dene.
        </p>
        <button
          type="button"
          onClick={() => reset()}
          style={{
            marginTop: "0.5rem",
            padding: "0.65rem 1.5rem",
            borderRadius: "9999px",
            border: "none",
            fontWeight: 600,
            cursor: "pointer",
            color: "#ffffff",
            background: "linear-gradient(135deg, #ff4d9a, #d6247a)",
          }}
        >
          Try again
        </button>
      </body>
    </html>
  );
}
